import { useState } from "react";
import { toast } from "sonner";
import { Building2, Handshake, Loader2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";
import { Field, SuccessPanel } from "@/components/FormBits";
import { api, formatApiError } from "@/lib/api";

const WAYS = [
  "Refer families who are ready to shop affordably",
  "Host a furniture or essentials drive",
  "Offer storage, truck, or moving support",
  "Sponsor pickups, deliveries, or cleaning supplies",
];

export default function Partners() {
  const [form, setForm] = useState({ name: "", business_name: "", location: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/partners", form);
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="partners-page">
      <PageHero overline="Community Partners" title="PARTNER WITH" italic="Fresh Start Furnishings." testid="partners-headline">
        Shelters, recovery programs, churches, realtors, moving companies, and local businesses — together we can turn
        more empty houses into homes across the West Valley.
      </PageHero>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        {done ? (
          <SuccessPanel
            testid="partner-success-message"
            title="THANK YOU FOR REACHING OUT!"
            message="Your partnership inquiry has been sent to our team. We'll be in touch soon to explore how we can work together."
          />
        ) : (
          <div className="grid gap-10 lg:grid-cols-2">
            <FadeUp>
              <div className="flex items-center gap-3">
                <Handshake className="h-8 w-8 text-burgundy" />
                <h2 className="font-serif text-2xl text-olive sm:text-3xl">Ways to partner</h2>
              </div>
              <ul className="mt-6 space-y-3">
                {WAYS.map((w, i) => (
                  <li key={w} data-testid={`partner-way-${i}`} className="flex items-start gap-3 rounded-xl border border-linen bg-white/60 p-4 text-sm text-moss">
                    <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-burgundy" />
                    {w}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm leading-relaxed text-sage">
                Tell us about your organization and how you'd like to help — every submission goes straight to our
                team at freshstartfurnishings@yahoo.com.
              </p>
            </FadeUp>
            <FadeUp delay={0.15}>
              <form onSubmit={submit} className="space-y-5 rounded-2xl border border-linen bg-white/60 p-8" data-testid="partner-form">
                <Field label="Your name" required testid="partner-name-field">
                  <input data-testid="partner-name-input" required className="input-field" value={form.name} onChange={set("name")} />
                </Field>
                <Field label="Business or organization name" required testid="partner-business-field">
                  <input data-testid="partner-business-input" required className="input-field" value={form.business_name} onChange={set("business_name")} />
                </Field>
                <Field label="Location" required testid="partner-location-field">
                  <input data-testid="partner-location-input" required className="input-field" placeholder="City, AZ" value={form.location} onChange={set("location")} />
                </Field>
                <Field label="Message" required testid="partner-message-field">
                  <textarea data-testid="partner-message-input" required rows={4} className="input-field"
                    placeholder="How would you like to partner with us?" value={form.message} onChange={set("message")} />
                </Field>
                <button type="submit" data-testid="partner-form-submit-button" disabled={submitting} className="btn-accent w-full !py-4 disabled:opacity-60">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                  {submitting ? "Sending…" : "Submit Partnership Inquiry"}
                </button>
              </form>
            </FadeUp>
          </div>
        )}
      </section>
    </div>
  );
}
